/**
 * @file cart.ts
 * @description Вид корзины сайта — раздел `theme.cart` документа оформления
 * (phase19.16, партия SB120, этап 4 «Корзина»; site-builder.md SB120, SB41;
 * проект данных docs/phases/phase19.16-materials/db-design-sb120-stage4.md
 * §1, проект UI uiux-cart.md §4, §8).
 *
 * Зачем отдельный модуль: схему раздела, умолчание «как сейчас» и
 * предупреждения кабинета читают схема темы (schema.ts), CSS (css.ts —
 * `--site-cart-*`), «Что изменено» (diff.ts), проверки окна публикации
 * (apps/api) и панель «Вид корзины» кабинета. Один источник подписей и
 * порогов для всех.
 *
 * Решения проекта данных:
 *   - `fill: null` — «как шапка» (заливка шапки), а не отдельный вариант
 *     заливки: функции заливок не меняются, им передаётся `fill ?? header.fill`
 *     (`cartEffectiveFill`); `mergeMissingKeys` `null` не трогает;
 *   - «Авто» у текста и суммы — значение `{ kind: "auto" }`
 *     (`siteAutoTextColorSchema`): у суммы умолчание — «Акцент», и «Авто»
 *     как отсутствие ключа не сохранилось бы;
 *   - тон корзины не хранится — считается при сборке CSS по заливке;
 *   - затемнение — целые проценты 0–80, в CSS идёт n/100;
 *   - `radius: null` — «как у сайта» (скругление карточек темы).
 * Умолчание совпадает с нынешней панелью CartDrawer.tsx у всех магазинов:
 * роли, а не цвета Chaiberi; #6a7282 — Tailwind gray-500 подложки.
 *
 * Предупреждения — вкус владельца, не запрет (uiux-cart §4): схема держит
 * только жёсткие пределы `SITE_THEME_LIMITS.cart`.
 */

import { z } from "zod";

import { SITE_CONTRAST_TEXT_MIN } from "./contrast.js";
import type { SiteColors } from "./keys.js";
import { siteSurfaceFillSchema, type SiteSurfaceFill } from "./surface-fill.js";
import { siteAutoTextColorSchema } from "./text-color.js";
import { autoTextOnFillRatio, floorContrastTenth } from "./text-on-fill.js";
import { SITE_THEME_LIMITS } from "./theme-limits.js";
import { deepFreeze } from "./deep-freeze.js";

const L = SITE_THEME_LIMITS.cart;

/** Целое в пределах: `int(L.widthPhone)`. */
const int = (limits: { readonly min: number; readonly max: number }): z.ZodNumber =>
  z.number().int().min(limits.min).max(limits.max);

/** Вид корзины: панель сбоку или окно из шапки. */
export const SITE_CART_MODES = ["drawer", "popover"] as const;
export type SiteCartMode = (typeof SITE_CART_MODES)[number];

/** Сторона панели. Окно на экране до 640 px — всегда справа (решение оркестратора). */
export const SITE_CART_SIDES = ["left", "right"] as const;
export type SiteCartSide = (typeof SITE_CART_SIDES)[number];

/** Что делать после «В корзину»: открыть корзину, окно на 4 с, только счётчик. */
export const SITE_CART_AFTER_ADD = ["open", "peek", "badge"] as const;
export type SiteCartAfterAdd = (typeof SITE_CART_AFTER_ADD)[number];

/** Подписи для кабинета (`OptionCards`, `Segmented`); дифф берёт их строчными. */
export const SITE_CART_MODE_LABELS: Readonly<Record<SiteCartMode, string>> = {
  drawer: "Панель сбоку",
  popover: "Окно из шапки",
};

export const SITE_CART_SIDE_LABELS: Readonly<Record<SiteCartSide, string>> = {
  left: "Слева",
  right: "Справа",
};

export const SITE_CART_AFTER_ADD_LABELS: Readonly<Record<SiteCartAfterAdd, string>> = {
  open: "Открыть корзину",
  peek: "Окно на 4 с",
  badge: "Только счётчик",
};

export const siteCartSchema = z.strictObject({
  // Первым ключом — проверка выкладки ищет `"cart":{"mode":…` в site-config.
  mode: z.enum(SITE_CART_MODES),
  drawer: z.strictObject({
    side: z.enum(SITE_CART_SIDES),
    /** Ширина панели до 640 px экрана, px (и окна на телефоне). */
    widthPhone: int(L.widthPhone),
    /** Ширина панели от 640 px экрана, px. */
    widthWide: int(L.widthWide),
    /** Затемнение под панелью, % (и под окном на телефоне). */
    dim: int(L.dim),
  }),
  popover: z.strictObject({
    width: int(L.popoverWidth),
    /** Строк в окне до прокрутки. */
    rows: int(L.popoverRows),
    dim: int(L.dim),
  }),
  /** Цвет подложки: роль-заливка или свой (альфа не кладётся на фон страницы). */
  overlayColor: siteSurfaceFillSchema,
  /** Заливка корзины; null — «как шапка». */
  fill: siteSurfaceFillSchema.nullable(),
  /** Текст и цены строк; «Авто» — по тону заливки. */
  text: siteAutoTextColorSchema,
  /** Заливка «Итого». */
  summaryFill: siteSurfaceFillSchema,
  /** Цвет суммы; «Авто» — по тону заливки «Итого». */
  totalColor: siteAutoTextColorSchema,
  /** Скругление фото и «Итого», px; null — «как у сайта» (`shapes.radius.card`). */
  radius: int(L.radius).nullable(),
  /** Размер фото товара в строке, px. */
  photo: int(L.photo),
  afterAdd: z.enum(SITE_CART_AFTER_ADD),
});
export type SiteCart = z.infer<typeof siteCartSchema>;

/** Как сейчас у всех магазинов (CartDrawer.tsx): роли, а не цвета Chaiberi; #6a7282 = Tailwind gray-500. */
export const DEFAULT_SITE_CART: SiteCart = deepFreeze({
  mode: "drawer",
  drawer: { side: "right", widthPhone: L.widthPhone.default, widthWide: L.widthWide.default, dim: L.dim.drawer },
  popover: { width: L.popoverWidth.default, rows: L.popoverRows.default, dim: L.dim.popover },
  overlayColor: { kind: "custom", color: "#6a7282" },
  fill: { kind: "role", role: "darkBg" },
  text: { kind: "auto" },
  summaryFill: { kind: "role", role: "pageBg" },
  totalColor: { kind: "role", role: "accent" },
  radius: L.radius.default,
  photo: L.photo.default,
  afterAdd: "open",
});

/** Что нужно от темы для корзины: цвета и заливка шапки («как шапка»). */
export interface SiteCartTheme {
  readonly colors: SiteColors;
  readonly header: { readonly fill: SiteSurfaceFill };
}

/** Заливка, которой корзина рисуется на самом деле: своя или шапки. */
export function cartEffectiveFill(cart: Pick<SiteCart, "fill">, theme: SiteCartTheme): SiteSurfaceFill {
  return cart.fill ?? theme.header.fill;
}

// ── Предупреждения (не запреты, uiux-cart §4) ──

export type SiteCartWarningCode =
  | "cart_phone_wide"
  | "cart_wide_wide"
  | "cart_rows_many"
  | "cart_no_dim"
  | "cart_photo_small"
  | "cart_badge_only"
  | "cart_text_contrast"
  | "cart_total_contrast";

/** Предупреждение: код, путь поля в теме (`cart.drawer.widthPhone`) и число для подписи. */
export interface SiteCartWarning {
  code: SiteCartWarningCode;
  path: string;
  /** Контраст (до десятых, с отбрасыванием) у кодов контраста. */
  value?: number;
}

/** Тексты словами владельца (uiux-cart §4, колонка «Предупреждение, не запрет»). */
export const SITE_CART_WARNING_MESSAGES: Readonly<Record<SiteCartWarningCode, string>> = {
  cart_phone_wide: "Шире 80 % экрана телефона — закрывает почти всю страницу.",
  cart_wide_wide: "Шире 560 px — на ноутбуке закрывает пол-экрана.",
  cart_rows_many: "Больше 6 строк — окно выше экрана ноутбука.",
  cart_no_dim: "Без затемнения страница под панелью отвлекает.",
  cart_photo_small: "Фото меньше 56 px трудно разглядеть на телефоне.",
  cart_badge_only: "Только счётчик: покупатель может не заметить добавление.",
  cart_text_contrast: "Контраст текста с заливкой ниже 4,5:1 — возьмите читаемый цвет.",
  cart_total_contrast: "Контраст суммы с заливкой «Итого» ниже 4,5:1 — возьмите читаемый цвет.",
};

/**
 * С какой ширины панели на телефоне предупреждать. Порог проекта — 288
 * (80 % от 360), но нынешняя панель — 300 (83 %), и витрина держит её и на
 * узком экране (`keepMin`). Предупреждать о виде, который владелец не
 * выбирал, а получил «как было», — ложное срабатывание на умолчании;
 * поэтому порог — большее из двух (предупреждение с 301 px).
 */
const PHONE_WIDE_FROM = Math.max(L.widthPhone.warnAbove, L.widthPhone.keepMin);

/**
 * Предупреждения вида корзины для кабинета и окна публикации. Пороги —
 * `SITE_THEME_LIMITS.cart`; ширина панели от 640 px и затемнение панели —
 * только у вида «Панель», строки — только у окна; ширина на телефоне — у
 * обоих видов (окно на телефоне берёт её же).
 */
export function cartLookWarnings(cart: SiteCart, theme: SiteCartTheme): SiteCartWarning[] {
  const out: SiteCartWarning[] = [];
  const drawer = cart.mode === "drawer";
  if (cart.drawer.widthPhone > PHONE_WIDE_FROM) out.push({ code: "cart_phone_wide", path: "cart.drawer.widthPhone" });
  if (drawer && cart.drawer.widthWide > L.widthWide.warnAbove) out.push({ code: "cart_wide_wide", path: "cart.drawer.widthWide" });
  if (!drawer && cart.popover.rows > L.popoverRows.warnAbove) out.push({ code: "cart_rows_many", path: "cart.popover.rows" });
  if (drawer && cart.drawer.dim === 0) out.push({ code: "cart_no_dim", path: "cart.drawer.dim" });
  if (cart.photo < L.photo.warnBelow) out.push({ code: "cart_photo_small", path: "cart.photo" });
  if (cart.afterAdd === "badge") out.push({ code: "cart_badge_only", path: "cart.afterAdd" });

  const text = autoTextOnFillRatio(cart.text, cartEffectiveFill(cart, theme), theme);
  if (text !== null && text < SITE_CONTRAST_TEXT_MIN) {
    out.push({ code: "cart_text_contrast", path: "cart.text", value: floorContrastTenth(text) });
  }
  const total = autoTextOnFillRatio(cart.totalColor, cart.summaryFill, theme);
  if (total !== null && total < SITE_CONTRAST_TEXT_MIN) {
    out.push({ code: "cart_total_contrast", path: "cart.totalColor", value: floorContrastTenth(total) });
  }
  return out;
}
